'use client';
import { AuthenticatedContext } from "@components/AuthorizationContext";
import { EmbedReportConfiguration } from "@components/PowerBI";
import { GroupItem, StateProfileSchema } from "@lib/definitions";
import { ReportParams } from "pbi-config";
import { B2C_LOGIN_TYPE, LOGIN_TYPE_STORAGE_NAME, TOKEN_EXPIRATION_TIME_STORAGE_NAME } from "clientConst";
import { FormState, IDataLayoutFieldItem, IIssuerItem, IPowerBIEntity, IPropertyType, IStateProfileDetail, ListItem, ReportEntityType } from "@definitions/index";
import { redirect } from "next/navigation";
import {
    CheckboxFormControl,
    DropdownFormControl,
    DatePickerFormControl,
    TextFormControl,
    ToggleFormControl,
    MultilineTextFormControl
} from "@components/FormControl";
import {
    NewDropdownFormControl,
    NewTextFormControl,
    NewMultilineTextFormControl,
    NewToggleFormControl,
    NewDatePickerFormControl,
    EmptyFormControl,
    NewRadioGroupFormControl,
    NewMultipleSelectFormControl,
    NewCheckboxFormControl,
    NewOnlyTextFormControl,
} from "@components/NewFormControl";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { EmbedReportVisualConfiguration } from "@components/PowerBI/EmbedVisual";
import { IReportVisual, IRlsRole, ISimpleDatasetIDs } from "@definitions/reportDefinitions";
import { DATE_FORMAT, DEFAULT_CURRENCY, DEFAULT_LOCALE, ISO_DATE_FORMAT, LONG_DATE_FORMAT, TRUE_ARRAY } from "@constants/common";
import NewAutoCompleteFormControl from "@components/NewFormControl/NewAutoCompleteFormControl";
import moment from "moment";
import NewHighlightedDatePickerFormControl from "@components/NewFormControl/NewHighlightedDatePickerFormControl";
import NewSeparateLabelDatePickerFormControl from "@components/NewFormControl/NewSeparateLabelDatePickerFormControl";
import NewNumberFormControl from "@components/NewFormControl/NewNumberFormControl";
import { Divider, FormControl, Grid, Typography } from "@mui/material";
import { paddingX1Props, requiredLabelStyle } from "@styles/global-css-class";
import { colors } from "@components/ThemeRegistry/theme";
import NewMultipleSelectSimpleFormControl from "@components/NewFormControl/NewMultipleSelectSimpleFormControl";
import { STAGING_PREFIX } from "@definitions/definitions";
import { DEFAULT_RISK_STATES, IPossMultiRetailExemptionItem } from "@definitions/issuerDefinitions";
import NewDatePickerWithOnAcceptFormControl from "@components/NewFormControl/NewDatePickerWithOnAcceptFormControl";

export const formatCurrency = (amount: any, currency = DEFAULT_CURRENCY, locale = DEFAULT_LOCALE) => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
    }).format(amount);
};

export const formatShortDateString = (dateString: string) => {
    return dateString ? moment(dateString, ISO_DATE_FORMAT).format(DATE_FORMAT) : null;
};

export const getCurrentDateString = (includeWeekday: boolean = false, locale = DEFAULT_LOCALE) => {
    const currentDate = new Date();
    return formatDateToLocalString(currentDate, includeWeekday, locale)
};

export const getCurrentSaturdayDate = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const saturday = new Date(today);
    saturday.setDate(today.getDate() + (6 - dayOfWeek));
    return formatDateToLocalString(saturday)
};

// get current Friday
export const getCurrentFridayDate = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const friday = new Date(today);
    friday.setDate(today.getDate() + (5 - dayOfWeek));
    return formatDateToLocalString(friday)
};

export const formatDateToLocalString = (
    date: Date,
    includeWeekday: boolean = false,
    locale: string = DEFAULT_LOCALE,
) => {
    return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: includeWeekday ? 'long' : undefined,
    });
};

const formatDateToLocal = (
    dateStr: string,
    locale: string = DEFAULT_LOCALE,
) => {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    };
    const formatter = new Intl.DateTimeFormat(locale, options);
    return formatter.format(date);
};

const generatePagination = (currentPage: number, totalPages: number) => {
    // If the total number of pages is 7 or less,
    // display all pages without any ellipsis.
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // If the current page is among the first 3 pages,
    // show the first 3, an ellipsis, and the last 2 pages.
    if (currentPage <= 3) {
        return [1, 2, 3, '...', totalPages - 1, totalPages];
    }

    // If the current page is among the last 3 pages,
    // show the first 2, an ellipsis, and the last 3 pages.
    if (currentPage >= totalPages - 2) {
        return [1, 2, '...', totalPages - 2, totalPages - 1, totalPages];
    }

    // If the current page is somewhere in the middle,
    // show the first page, an ellipsis, the current page and its neighbors,
    // another ellipsis, and the last page.
    return [
        1,
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        totalPages,
    ];
};

// string compare helper equivalent to .NET's string.Equals(a, b, StringComparison.OrdinalIgnoreCase)
const equalsOrdinalIgnoreCase = (a?: string | null, b?: string | null) =>
    (a ?? '').toUpperCase() === (b ?? '').toUpperCase();

const checkRole = (authenticatedContext: AuthenticatedContext, authorityRoles: string[], checkAADTenant: boolean = false) => {
    const userGroups = authenticatedContext.userGroups;
    const activeAccount = authenticatedContext.activeAccount;
    if (userGroups && userGroups?.length > 0) {
        const hasRole = userGroups.some(userGroup => authorityRoles.some(role => equalsOrdinalIgnoreCase(role, userGroup.roleName)));
        if (checkAADTenant && activeAccount.tenantId) {
            return hasRole && activeAccount.tenantId === process.env.NEXT_PUBLIC_AAD_TENANT_ID!;
        }
        return hasRole;
    }
    return false;
}

const checkRoleAdmin = (userGroups: GroupItem[], authorityRoles: string[]) => {
    if (userGroups && userGroups?.length > 0) {
        return userGroups.some(userGroup => authorityRoles.some(role => equalsOrdinalIgnoreCase(role, userGroup.roleName)));
    }
    return false;
}

export const convertEmbedReportConfigurationFromPowerBIEntity = (reportEntity: IPowerBIEntity): EmbedReportConfiguration => {
    return {
        id: reportEntity?.entityIDs.filter(
            (id) => id.entityType == ReportEntityType.Report
        )[0]?.entityID,
        workspaceId: reportEntity?.workspaceId,
        paginatedReportId: reportEntity?.entityIDs
            .filter((id) => id.entityType == ReportEntityType.PaginatedReport)
            .map((id) => id.entityID)
            .join(";"),
        datasetId: reportEntity?.datasetIDs
            .map((dataset) => dataset.datasetID)
            .join(";"),
        name: reportEntity.name,
        rlsRoles: getRlsRolesFromDatasetIDs(reportEntity?.datasetIDs, reportEntity?.fileName),
        ...ReportParams
    } as EmbedReportConfiguration;
}

export const convertReportVisualToEmbedConfiguration = (reportVisual: IReportVisual): EmbedReportConfiguration => {
    return {
        id: reportVisual.reportId,
        workspaceId: reportVisual.workspaceId,
        name: reportVisual.visualTitle,
        type: "visual",
        cssClassName: "report-visual-embed-container",
        rlsRoles: getRlsRolesFromDatasetIDs(reportVisual?.datasetIDs),
    } as EmbedReportConfiguration;
}

export const getRlsRolesFromDatasetIDs = (datasetIDs: ISimpleDatasetIDs[] | undefined, reportFileName?: string): IRlsRole[] => {
    if (!datasetIDs) {
        return [];
    }

    if (reportFileName) {
        return datasetIDs
            .filter((dataset) => (dataset.name?.startsWith("Model_") || dataset.name === reportFileName) && dataset.rlsRole)
            .map((dataset) => {
                return { datasetId: dataset.datasetID, role: dataset.rlsRole } as IRlsRole;
            });
    }

    return datasetIDs
        .filter((dataset) => dataset.rlsRole)
        .map((dataset) => {
            return { datasetId: dataset.datasetID, role: dataset.rlsRole } as IRlsRole;
        });
}

export const findReportVisualByKey = (reportEntity: IReportVisual[], key: string, embedToken: string, embedUrl: string): EmbedReportVisualConfiguration | undefined => {
    const visual = reportEntity?.find((v: IReportVisual) => v.key === key)
    if (!visual) {
        return undefined;
    }
    return {
        id: visual.reportId,
        pageName: visual.pageName,
        visualName: visual.visualName,
        cssClassName: "report-visual-embed-container",
        embedToken: embedToken,
        embedUrl: embedUrl,
    };
}

const localeCompare = (a: string, b: string) => a.localeCompare(b);

const trimTwoUppercaseCharsAtBeginning = (text: string) => {
    if (text == undefined) {
        return "";
    }
    const firstLowerCaseIndex = text.search(/[a-z]/);
    return text.substring(firstLowerCaseIndex - 1);
};

export const setLoginType = (type: string) => {
    if (!localStorage) return;
    localStorage.setItem(LOGIN_TYPE_STORAGE_NAME, type);
}

export const getLoginType = () => {
    if (!localStorage) return "";
    return localStorage.getItem(LOGIN_TYPE_STORAGE_NAME);
}

export const getTokenExpirationTime = () => {
    if (!localStorage) return 0;
    return Number.parseInt(localStorage.getItem(TOKEN_EXPIRATION_TIME_STORAGE_NAME) || "0");
}

export const setTokenExpirationTime = (expTime?: any) => {
    if (!expTime) {
        return;
    }
    localStorage.setItem(TOKEN_EXPIRATION_TIME_STORAGE_NAME, `${expTime}`);
}


export const isB2cUserLoggingIn = () => {
    //Check if user is logged in as B2C
    if (typeof window !== 'undefined') {
        return localStorage.getItem(LOGIN_TYPE_STORAGE_NAME) === B2C_LOGIN_TYPE;

    }
    return false;
}

const currencyFormat = (value: number | null) => '$ ' + (value ?? 0);

export const currencyFormatUsd = (params: any) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(params.value);
}

export const dateFormatter = (params: any) => {
    return params.value ? moment(params.value).format(DATE_FORMAT) : '';
}

export const longDateFormatter = (params: any) => {
    return params.value ? moment(params.value).format(LONG_DATE_FORMAT) : '';
}

const monthNameToDateTimeString = (monthName: string): string => {
    const monthMap: { [key: string]: number } = {
        "January": 1,
        "February": 2,
        "March": 3,
        "April": 4,
        "May": 5,
        "June": 6,
        "July": 7,
        "August": 8,
        "September": 9,
        "October": 10,
        "November": 11,
        "December": 12
    };
    const monthNumber = monthMap[monthName];
    const date = new Date(1904, monthNumber, 1);
    const dateString = date.toISOString().slice(0, 10) + "T00:00:00";
    return dateString;
}

const dateTimeStringToMonthName = (dateTimeString: string | null): string | undefined => {
    if (dateTimeString == null) {
        return undefined;
    }
    const monthNumber = new Date(dateTimeString).getUTCMonth();
    const monthNames: string[] = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    const monthName = monthNames[monthNumber];
    return monthName;
}

export const isCurrentUserInternalAdmin = (authenticatedContext: AuthenticatedContext) => {
    return authenticatedContext.isAADTenant == true && authenticatedContext.isAdminRole == true;
}

export const checkIsAdmin = (authenticatedContext: AuthenticatedContext) => {
    if (!authenticatedContext.isAADTenant) {
        redirect("/unauthorized");
    }
}
export const checkIsInternalUser = (authenticatedContext: AuthenticatedContext) => {
    if (!authenticatedContext.isAADTenant) {
        redirect("/unauthorized");
    }
}

export const getListItemsOfCombinedIssuers = (listIssuers: IIssuerItem[]) => {
    if (!listIssuers || listIssuers.length === 0) {
        return [];
    }
    return listIssuers.map((issuer) => getCombinedIssuerListItem(issuer));
}

export const getCombinedIssuerListItem = (issuer: IIssuerItem) => {
    if (issuer.isStaging) {
        return {
            value: `${STAGING_PREFIX}${issuer.id}`,
            name: `${issuer.name} (staging)`,
        } as ListItem;
    } else {
        return {
            value: issuer.id.toString(),
            name: issuer.name,
        } as ListItem
    }
}

export const getDefaultRiskStates = (currentStates?: IPossMultiRetailExemptionItem[]) => {
    if (!currentStates || currentStates.length == 0) {
        return DEFAULT_RISK_STATES;
    }

    const notExists = DEFAULT_RISK_STATES.filter((x) => !currentStates.some((s) => s.stateId == x.stateId))
    return [...currentStates, ...notExists]
}

export const isInheritedValuesDifferent = (type: 'boolean' | 'number' | 'string' | 'string[]', firstValue: any, secondValue: any) => {
    if (type === 'boolean') {
        return TRUE_ARRAY.includes(firstValue) !== TRUE_ARRAY.includes(secondValue);
    } else if (type === 'number') {
        return (firstValue ?? 0) !== (secondValue ?? 0);
    } else if (type === 'string') {
        return (firstValue ?? '') !== (secondValue ?? '');
    } else if (type === 'string[]') {
        if (!Array.isArray(firstValue) || !Array.isArray(secondValue)) {
            return true;
        }
        if (firstValue.length !== secondValue.length) {
            return true;
        }
        return firstValue.some((value, index) => value !== secondValue[index]);
    }
    return false;
};

export const redirectClient = (url: string, isPush?: boolean) => {
    // redirect(url, isPush ? RedirectType.push : RedirectType.replace);
    location.href = url;
}

const showFormFields = (data: any, formFields: IPropertyType[]) => {
    return formFields.map(formField => {
        if (formField.isHidden) {
            return;
        }
        const nameField = formField.name;
        let formattedValue: any = undefined;
        if (data) {
            formattedValue = formField.formatValue ? formField.formatValue(data[nameField]) : data[nameField];
        }
        switch (formField.type) {
            case "string":
            case "number":
                return <TextFormControl
                    key={nameField}
                    name={formField.name}
                    label={formField.text}
                    textAlign={formField.textAlign}
                    value={formattedValue}
                    onChange={formField.onChange}
                    isNumberInput={formField.type === "number"}
                    decimalDigits={formField.decimalDigits}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled}
                    xsLabel={formField.xsLabel}
                    itemSx={formField.itemSx}
                    formState={formField.formState}
                    isHidden={formField.isHidden}
                    textSx={formField.textSx}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    placeHolder={formField.placeHolder}
                    onlyNumber={formField.onlyNumber}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    isHideLabel={formField.isHideLabel}
                    showIndicator={formField.showIndicator}
                />;
            case "boolean":
                return <ToggleFormControl
                    key={nameField}
                    name={formField.name}
                    value={formattedValue}
                    onChange={formField.onChange}
                    label={formField.text}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled}
                    formState={formField.formState}
                    xsLabel={formField.xsLabel}
                    textSx={formField.textSx}
                    isHidden={formField.isHidden}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    showIndicator={formField.showIndicator}
                />
            case "list":
                return <DropdownFormControl
                    key={nameField}
                    name={formField.name}
                    label={formField.text}
                    defaultValue={formField.defaultValue}
                    value={formattedValue}
                    onChange={formField.onChange}
                    isRequired={formField.isRequired}
                    listValues={formField.listValues ?? []}
                    isDisabled={formField.isDisabled}
                    zIndex={formField.zIndex}
                    xsLabel={formField.xsLabel}
                    itemSx={formField.itemSx}
                    formState={formField.formState}
                    textSx={formField.textSx}
                    isHidden={formField.isHidden}
                    textAlign={formField.textAlign}
                    textErrorAlign={formField.textErrorAlign}
                    placeHolder={formField.placeHolder}
                    showSelect={formField.showSelect}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                />
            case "date":
                return <DatePickerFormControl
                    key={nameField}
                    name={formField.name}
                    label={formField.text}
                    value={formattedValue ?? null}
                    onChange={formField.onChange}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled}
                    xsLabel={formField.xsLabel}
                    formState={formField.formState}
                    textSx={formField.textSx}
                    isHidden={formField.isHidden}
                    textAlign={formField.textAlign}
                    textErrorAlign={formField.textErrorAlign}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                />
            case "multiple":
                return <MultilineTextFormControl
                    key={nameField}
                    name={formField.name}
                    label={formField.text}
                    value={formattedValue}
                    onChange={formField.onChange}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled}
                    isHidden={formField.isHidden}
                    xsLabel={formField.xsLabel}
                    itemSx={formField.itemSx}
                    formState={formField.formState}
                    textSx={formField.textSx}
                    textAlign={formField.textAlign}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    placeHolder={formField.placeHolder}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                />
            case "checkbox":
                return <CheckboxFormControl
                    key={nameField}
                    name={formField.name}
                    value={formattedValue}
                    onChange={formField.onChange}
                    label={formField.text}
                    textAlign={formField.textAlign}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled}
                    formState={formField.formState}
                    xsLabel={formField.xsLabel}
                    textSx={formField.textSx}
                    isHidden={formField.isHidden}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                />
            default:
                return;
        }
    });
}

const showNewFormFields = (data: any, formFields: IPropertyType[], formState?: FormState, isReadOnly?: boolean, listOrderFocus?: string[], focusFieldName?: string) => {
    return formFields?.map((formField, index) => {
        if (formField.isHidden) {
            return '';
        }
        const nameField = formField.name;
        let formattedValue: any = undefined;
        if (data) {
            formattedValue = formField.formatValue ? formField.formatValue(data[nameField]) : data[nameField];
        }

        let isExpanded = formField.isExpanded;

        if (formState?.errors &&
            Object.keys(formState.errors).length > 0 &&
            listOrderFocus && formState?.errors[formField.errorName ?? nameField] !== undefined) {
            isExpanded = Object.keys(formState.errors).find(e => listOrderFocus.slice(0, listOrderFocus.indexOf(formField.errorName ?? nameField)).includes(e)) === undefined;
        }

        let minDate = formField.minDate;
        let maxDate = formField.maxDate;
        let openDate = undefined;
        let isDisable = formField.isDisabled || isReadOnly;
        if (formField.calculateMinDate) {
            minDate = formField.calculateMinDate(data);
        }
        if (formField.calculateMaxDate) {
            maxDate = formField.calculateMaxDate(data);
        }
        if (formField.calculateOpenDate) {
            openDate = formField.calculateOpenDate(data);
        }
        if (formField.calculateDisable) {
            isDisable = isDisable || formField.calculateDisable(data);
        }

        switch (formField.type) {
            case "string":
                return <NewTextFormControl
                    key={`${formField.childKey ?? nameField}-${index}`}
                    name={formField.name}
                    label={formField.text}
                    textAlign={formField.textAlign}
                    value={formattedValue ?? formField.defaultValue}
                    onChange={formField.onChange}
                    onBlur={formField.onBlur}
                    decimalDigits={formField.decimalDigits}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled || isReadOnly}
                    xsLabel={formField.xsLabel}
                    itemSx={formField.itemSx}
                    formState={formState}
                    isHidden={formField.isHidden}
                    textSx={formField.textSx}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    placeHolder={formField.placeHolder}
                    onlyNumber={formField.onlyNumber}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    isExpanded={isExpanded}
                    itemFocus={focusFieldName === nameField}
                    childKey={formField.childKey ?? nameField}
                    isHideLabel={formField.isHideLabel}
                    labelToolTip={formField.labelToolTip}
                    hasEndAdornment={formField.hasEndAdornment}
                    endAdornmentLabel={formField.endAdornmentLabel}
                    errorName={formField.errorName}
                    showIndicator={formField.showIndicator}
                    isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                />;
            case "onlyString":
                return <NewOnlyTextFormControl
                    key={`${formField.childKey ?? nameField}-${index}`}
                    name={formField.name}
                    label={formField.text}
                    textAlign={formField.textAlign}
                    value={formattedValue ?? formField.defaultValue}
                    onChange={formField.onChange}
                    onBlur={formField.onBlur}
                    isRequired={formField.isRequired}
                    isDisabled={isDisable}
                    xsLabel={formField.xsLabel}
                    itemSx={formField.itemSx}
                    formState={formState}
                    isHidden={formField.isHidden}
                    textSx={formField.textSx}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    placeHolder={formField.placeHolder}
                    onlyNumber={formField.onlyNumber}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    isExpanded={isExpanded}
                    itemFocus={focusFieldName === nameField}
                    childKey={formField.childKey ?? nameField}
                    isHideLabel={formField.isHideLabel}
                    labelToolTip={formField.labelToolTip}
                    hasEndAdornment={formField.hasEndAdornment}
                    endAdornmentLabel={formField.endAdornmentLabel}
                    formatValue={formField.formatValue}
                    errorName={formField.errorName}
                    showIndicator={formField.showIndicator}
                    isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                    isReturnId={formField.isReturnId}
                    id={data?.id}
                />;
            case "number":
                return <NewNumberFormControl
                    key={`${formField.childKey ?? nameField}-${index}`}
                    name={formField.name}
                    label={formField.text}
                    textAlign={formField.textAlign}
                    value={formattedValue ?? formField.defaultValue}
                    onChange={formField.onChange}
                    decimalDigits={formField.decimalDigits}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled || isReadOnly}
                    xsLabel={formField.xsLabel}
                    itemSx={formField.itemSx}
                    formState={formState}
                    isHidden={formField.isHidden}
                    textSx={formField.textSx}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    placeHolder={formField.placeHolder}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    isExpanded={isExpanded}
                    childKey={formField.childKey ?? nameField}
                    isHideLabel={formField.isHideLabel}
                    labelToolTip={formField.labelToolTip}
                    endAdornmentLabel={formField.endAdornmentLabel}
                    thousandSeparator={formField.thousandSeparator}
                    allowLeadingZeros={formField.allowLeadingZeros}
                    fixedDecimalScale={formField.fixedDecimalScale}
                    allowNegative={formField.allowNegative}
                    decimalSeparator={formField.decimalSeparator}
                    showIndicator={formField.showIndicator}
                    errorName={formField.errorName}
                    description={formField.description}
                    isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                />;
            case "boolean":
                return <NewToggleFormControl
                    key={`${formField.childKey ?? nameField}-${index}`}
                    name={formField.name}
                    value={formattedValue ?? formField.defaultValue}
                    onChange={formField.onChange}
                    label={formField.text}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled || isReadOnly}
                    formState={formState}
                    xsLabel={formField.xsLabel}
                    textSx={formField.textSx}
                    isHidden={formField.isHidden}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    isExpanded={isExpanded}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    itemSx={formField.itemSx}
                    childKey={`${formField.childKey ?? nameField}-${index}`}
                    errorName={formField.errorName}
                    isShowRequiredAsterisk={formField.isShowRequiredAsterisk}
                    showIndicator={formField.showIndicator}
                    isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                />
            case "list":
                return <NewDropdownFormControl
                    key={`${formField.childKey ?? nameField}-${index}`}
                    name={formField.name}
                    label={formField.text}
                    defaultValue={formField.defaultValue}
                    value={formattedValue ?? formField.defaultValue ?? ' '}
                    onChange={formField.onChange}
                    isRequired={formField.isRequired}
                    listValues={formField.listValues ?? []}
                    isDisabled={formField.isDisabled || isReadOnly}
                    zIndex={formField.zIndex}
                    xsLabel={formField.xsLabel}
                    itemSx={formField.itemSx}
                    formState={formState}
                    textSx={formField.textSx}
                    isHidden={formField.isHidden}
                    textAlign={formField.textAlign}
                    textErrorAlign={formField.textErrorAlign}
                    placeHolder={formField.placeHolder}
                    showSelect={formField.showSelect}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    errorName={formField.errorName}
                    isExpanded={isExpanded}
                    allowUndefinedDefaultSelect={formField.allowUndefinedDefaultSelect}
                    childKey={formField.childKey ?? nameField}
                    isDisableDefaultSelect={formField.isDisableDefaultSelect}
                    inputSx={formField.inputSx}
                    isHideLabel={formField.isHideLabel}
                    valueDefaultSelect={formField.valueDefaultSelect}
                    isShowRequiredAsterisk={formField.isShowRequiredAsterisk}
                    showIndicator={formField.showIndicator}
                    showSearchBox={formField.showSearchBox}
                    isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                />
            case "autoComplete":
                return <NewAutoCompleteFormControl
                    key={`${formField.childKey ?? nameField}-${index}`}
                    name={formField.name}
                    label={formField.text}
                    defaultValue={formField.defaultValue}
                    value={formattedValue ?? formField.defaultValue}
                    onChange={formField.onChange}
                    isRequired={formField.isRequired}
                    listValues={formField.listValues ?? []}
                    isDisabled={formField.isDisabled || isReadOnly}
                    zIndex={formField.zIndex}
                    xsLabel={formField.xsLabel}
                    itemSx={formField.itemSx}
                    formState={formState}
                    textSx={formField.textSx}
                    isHidden={formField.isHidden}
                    textAlign={formField.textAlign}
                    textErrorAlign={formField.textErrorAlign}
                    placeHolder={formField.placeHolder}
                    showSelect={formField.showSelect}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    isExpanded={isExpanded}
                    isFreeSolo={formField.isFreeSolo}
                    childKey={formField.childKey ?? nameField}
                    errorName={formField.errorName}
                    disableClearable={formField.disableClearable}
                    showIndicator={formField.showIndicator}
                    isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                />
            case "date":
                return formField.highlightedDays ?
                    <NewHighlightedDatePickerFormControl
                        key={`${formField.childKey ?? nameField}-${index}`}
                        name={formField.name}
                        label={formField.text}
                        value={formattedValue ?? null}
                        onChange={formField.onChange}
                        isRequired={formField.isRequired}
                        isDisabled={formField.isDisabled || isReadOnly}
                        xsLabel={formField.xsLabel}
                        formState={formState}
                        textSx={formField.textSx}
                        isHidden={formField.isHidden}
                        textAlign={formField.textAlign}
                        textErrorAlign={formField.textErrorAlign}
                        xsInput={formField.xsInput}
                        xsFormControl={formField.xsFormControl}
                        mdFormControl={formField.mdFormControl}
                        itemSx={formField.itemSx}
                        defaultValue={formField.defaultValue}
                        isExpanded={isExpanded}
                        childKey={formField.childKey ?? nameField}
                        errorName={formField.errorName}
                        highlightedDays={formField.highlightedDays}
                        showIndicator={formField.showIndicator}
                        isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                    /> : (formField.isShowSeparateLabel ?
                        <NewSeparateLabelDatePickerFormControl
                            key={`${formField.childKey ?? nameField}-${index}`}
                            name={formField.name}
                            label={formField.text}
                            value={formattedValue ?? null}
                            onChange={formField.onChange}
                            isRequired={formField.isRequired}
                            isDisabled={formField.isDisabled || isReadOnly}
                            xsLabel={formField.xsLabel}
                            formState={formState}
                            textSx={formField.textSx}
                            isHidden={formField.isHidden}
                            textAlign={formField.textAlign}
                            textErrorAlign={formField.textErrorAlign}
                            xsInput={formField.xsInput}
                            xsFormControl={formField.xsFormControl}
                            mdFormControl={formField.mdFormControl}
                            itemSx={formField.itemSx}
                            defaultValue={formField.defaultValue}
                            isExpanded={isExpanded}
                            childKey={formField.childKey ?? nameField}
                            formatDate={formField.formatDate}
                            isShowSeparateLabel={formField.isShowSeparateLabel}
                            separateLabel={formField.separateLabel}
                            datePickerViews={formField.datePickerViews}
                            isHideHeader={formField.isHideHeader}
                            errorName={formField.errorName}
                            openTo={formField.openTo}
                            showIndicator={formField.showIndicator}
                            isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                        /> : (formField.returnOnAccept ? <NewDatePickerWithOnAcceptFormControl
                            key={`${formField.childKey ?? nameField}-${index}`}
                            name={formField.name}
                            label={formField.text}
                            value={formattedValue ?? null}
                            onChange={formField.onChange}
                            isRequired={formField.isRequired}
                            isDisabled={isDisable}
                            xsLabel={formField.xsLabel}
                            formState={formState}
                            textSx={formField.textSx}
                            isHidden={formField.isHidden}
                            textAlign={formField.textAlign}
                            textErrorAlign={formField.textErrorAlign}
                            xsInput={formField.xsInput}
                            xsFormControl={formField.xsFormControl}
                            mdFormControl={formField.mdFormControl}
                            itemSx={formField.itemSx}
                            defaultValue={formField.defaultValue}
                            isExpanded={isExpanded}
                            childKey={formField.childKey ?? nameField}
                            formatDate={formField.formatDate}
                            shouldDisableMonth={formField.shouldDisableMonth}
                            shouldDisableYear={formField.shouldDisableYear}
                            shouldDisableDate={formField.shouldDisableDate}
                            datePickerViews={formField.datePickerViews}
                            minDate={minDate}
                            maxDate={maxDate}
                            errorName={formField.errorName}
                            isReturnId={formField.isReturnId}
                            id={data?.id}
                            openDate={openDate}
                            showIndicator={formField.showIndicator}
                            isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                        /> : <NewDatePickerFormControl
                            key={`${formField.childKey ?? nameField}-${index}`}
                            name={formField.name}
                            label={formField.text}
                            value={formattedValue ?? null}
                            onChange={formField.onChange}
                            isRequired={formField.isRequired}
                            isDisabled={isDisable}
                            xsLabel={formField.xsLabel}
                            formState={formState}
                            textSx={formField.textSx}
                            isHidden={formField.isHidden}
                            textAlign={formField.textAlign}
                            textErrorAlign={formField.textErrorAlign}
                            xsInput={formField.xsInput}
                            xsFormControl={formField.xsFormControl}
                            mdFormControl={formField.mdFormControl}
                            itemSx={formField.itemSx}
                            defaultValue={formField.defaultValue}
                            isExpanded={isExpanded}
                            childKey={formField.childKey ?? nameField}
                            formatDate={formField.formatDate}
                            shouldDisableMonth={formField.shouldDisableMonth}
                            shouldDisableYear={formField.shouldDisableYear}
                            shouldDisableDate={formField.shouldDisableDate}
                            datePickerViews={formField.datePickerViews}
                            minDate={minDate}
                            maxDate={maxDate}
                            errorName={formField.errorName}
                            isReturnId={formField.isReturnId}
                            id={data?.id}
                            openDate={openDate}
                            showIndicator={formField.showIndicator}
                            isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                        />))
            case "multiple":
                return <NewMultilineTextFormControl
                    key={`${formField.childKey ?? nameField}-${index}`}
                    name={formField.name}
                    label={formField.text}
                    value={formattedValue ?? formField.defaultValue}
                    onChange={formField.onChange}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled || isReadOnly}
                    isHidden={formField.isHidden}
                    xsLabel={formField.xsLabel}
                    itemSx={formField.itemSx}
                    formState={formState}
                    textSx={formField.textSx}
                    textAlign={formField.textAlign}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    placeHolder={formField.placeHolder}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    isExpanded={isExpanded}
                    errorName={formField.errorName}
                    childKey={formField.childKey ?? nameField}
                    showIndicator={formField.showIndicator}
                    isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                />
            case "checkbox":
                return <NewCheckboxFormControl
                    key={`${formField.childKey ?? nameField}-${index}`}
                    name={formField.name}
                    value={formattedValue ?? formField.defaultValue}
                    onChange={formField.onChange}
                    label={formField.text}
                    textAlign={formField.textAlign}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled || isReadOnly}
                    formState={formState}
                    textSx={formField.textSx}
                    isHidden={formField.isHidden}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    itemSx={formField.itemSx}
                    isExpanded={isExpanded}
                    childKey={formField.childKey ?? nameField}
                    checkBoxSx={formField.checkBoxSx}
                    isHideLabel={formField.isHideLabel}
                    isShowRequiredAsterisk={formField.isShowRequiredAsterisk}
                    errorName={formField.errorName}
                    showIndicator={formField.showIndicator}
                    isIndicatorTakeSpace={formField.isIndicatorTakeSpace}
                />
            case "radio":
                return <NewRadioGroupFormControl
                    key={`${formField.childKey ?? nameField}-${index}`}
                    name={formField.name}
                    value={formattedValue ?? formField.defaultValue}
                    onChange={formField.onChange}
                    label={formField.text}
                    textAlign={formField.textAlign}
                    isRequired={formField.isRequired}
                    isDisabled={formField.isDisabled || isReadOnly}
                    formState={formState}
                    xsLabel={formField.xsLabel}
                    textSx={formField.textSx}
                    isHidden={formField.isHidden}
                    defaultValue={formField.defaultValue}
                    textErrorAlign={formField.textErrorAlign}
                    xsInput={formField.xsInput}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    listValues={formField.listValues}
                    isExpanded={isExpanded}
                    itemSx={formField.itemSx}
                    isVertical={formField.isVertical}
                    childKey={formField.childKey ?? nameField}
                    isLabelToolTip={formField.isLabelToolTip}
                    isInputToolTip={formField.isInputToolTip}
                    labelToolTip={formField.labelToolTip}
                    labelPlacement={formField.labelPlacement}
                    radioInline={formField.radioInline}
                    errorName={formField.errorName}
                    isHideLabel={formField.isHideLabel}
                    isShowRequiredAsterisk={formField.isShowRequiredAsterisk}
                    showIndicator={formField.showIndicator}
                />
            case "multipleSelect":
                return formField.isHasFilterMultipleOption ?
                    <NewMultipleSelectFormControl
                        key={`${formField.childKey ?? nameField}-${index}`}
                        name={formField.name}
                        value={formattedValue ?? formField.defaultValue}
                        onChange={formField.onChange}
                        label={formField.text}
                        textAlign={formField.textAlign}
                        isRequired={formField.isRequired}
                        isDisabled={formField.isDisabled || isReadOnly}
                        formState={formState}
                        xsLabel={formField.xsLabel}
                        textSx={formField.textSx}
                        isHidden={formField.isHidden}
                        defaultValue={formField.defaultValue}
                        textErrorAlign={formField.textErrorAlign}
                        isExpanded={isExpanded}
                        xsInput={formField.xsInput}
                        zIndex={formField.zIndex}
                        xsFormControl={formField.xsFormControl}
                        mdFormControl={formField.mdFormControl}
                        listValues={formField.listValues ?? []}
                        errorName={formField.errorName}
                        itemSx={formField.itemSx}
                        childKey={formField.childKey ?? nameField}
                        isHideLabel={formField.isHideLabel}
                        isShowRequiredAsterisk={formField.isShowRequiredAsterisk}
                        showIndicator={formField.showIndicator}
                        showAllSelection={formField.showAllSelection}
                    />
                    : <NewMultipleSelectSimpleFormControl
                        key={`${formField.childKey ?? nameField}-${index}`}
                        name={formField.name}
                        value={formattedValue ?? formField.defaultValue}
                        onChange={formField.onChange}
                        label={formField.text}
                        textAlign={formField.textAlign}
                        isRequired={formField.isRequired}
                        isDisabled={formField.isDisabled || isReadOnly}
                        formState={formState}
                        xsLabel={formField.xsLabel}
                        textSx={formField.textSx}
                        isHidden={formField.isHidden}
                        defaultValue={formField.defaultValue}
                        textErrorAlign={formField.textErrorAlign}
                        isExpanded={isExpanded}
                        xsInput={formField.xsInput}
                        zIndex={formField.zIndex}
                        xsFormControl={formField.xsFormControl}
                        mdFormControl={formField.mdFormControl}
                        listValues={formField.listValues ?? []}
                        errorName={formField.errorName}
                        itemSx={formField.itemSx}
                        childKey={formField.childKey ?? nameField}
                        isHideLabel={formField.isHideLabel}
                        isShowRequiredAsterisk={formField.isShowRequiredAsterisk}
                        showIndicator={formField.showIndicator}
                    />
            case "empty":
                return <EmptyFormControl
                    key={`${formField.childKey ?? nameField}-empty-${index}`}
                    name={nameField}
                    label={formField.text}
                    value={formattedValue}
                    isRequired={formField.isRequired}
                    onChange={formField.onChange}
                    xsFormControl={formField.xsFormControl}
                    mdFormControl={formField.mdFormControl}
                    itemSx={formField.itemSx}
                    childKey={formField.childKey ?? nameField}
                />;
            case "typography":
                return <Grid key={`${formField.childKey ?? nameField}-typography-${index}`} container item xs={formField.xsFormControl} md={formField.mdFormControl} sx={formField.itemSx ?? {
                    ...paddingX1Props,
                    alignContent: 'flex-start'
                }}>
                    <FormControl fullWidth margin='normal'>
                        <Typography
                            component={'span'}
                            sx={{
                                ...requiredLabelStyle,
                                color: colors.black
                            }}>
                            {formField.text}
                        </Typography>
                    </FormControl>
                </Grid>;
            case "divider":
                return <Grid key={`${formField.childKey ?? nameField}-divider-${index}`} container item xs={formField.xsFormControl} md={formField.mdFormControl} sx={formField.itemSx ?? {
                    ...paddingX1Props,
                    paddingTop: 1,
                    alignContent: 'flex-start'
                }}><Divider sx={{ borderColor: "#00000026", width: "100%" }} />
                </Grid>;
            case "element":
                return <Grid key={`${formField.childKey ?? nameField}-element-${index}`} container item xs={formField.xsFormControl} md={formField.mdFormControl} sx={formField.itemSx ?? {
                    ...paddingX1Props,
                    paddingTop: 1,
                    alignContent: 'flex-start'
                }}>
                    {formField.children}
                </Grid>;
            default:
                return '';
        }
    }) ?? '';
}

const isValidStateProfile = (stateProfile: IStateProfileDetail | null) => {
    const validStateProfile = StateProfileSchema.safeParse(stateProfile);
    return validStateProfile.success;
}

const redirectPathPage = (router: AppRouterInstance, url: string) => {
    const relativePath = url.replace(window.location.origin, '');
    return router.push(relativePath);
}

const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    // Included for legacy support, e.g. Chrome/Edge < 119
    event.returnValue = '';
};

export const addBeforeUnloadHandler = () => {
    window.addEventListener("beforeunload", beforeUnloadHandler);
};

export const removeBeforeUnloadHandler = () => {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
};

export const getFirstDateOfCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
}

export const getLastDateOfCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
}

// Default "Index" (fieldIndexNumber) or "Starting Position" for a new/edited DataLayoutField row,
// scoped to rows sharing the same Record Type so different record types (Header/Data/Trailer) can
// reuse the same positions independently.
export const getDefaultFieldPosition = (
    recordType: string,
    fields: IDataLayoutFieldItem[],
    isFixedWidth: boolean,
    excludeIndex?: number | null
): number => {
    const matches = (fields ?? []).filter(f =>
        f.recordType === recordType &&
        (excludeIndex === undefined || excludeIndex === null || f.index !== excludeIndex)
    );
    if (matches.length === 0) {
        return 1;
    }
    if (isFixedWidth) {
        const maxRow = matches.reduce((max, f) => (f.startingPosition ?? 0) > (max.startingPosition ?? 1) ? f : max);
        return (maxRow.startingPosition ?? 0) + (maxRow.maximumLength ?? 1);
    }
    const maxRow = matches.reduce((max, f) => (f.fieldIndexNumber ?? 0) > (max.fieldIndexNumber ?? 0) ? f : max);
    return (maxRow.fieldIndexNumber ?? 0) + 1;
}

// Standard interval-overlap test: two closed ranges [aStart, aStart+aLength-1] and
// [bStart, bStart+bLength-1] intersect iff aStart <= bEnd && bStart <= aEnd.
export const doRangesOverlap = (
    aStart: number, aLength: number,
    bStart: number, bLength: number
): boolean => {
    const aEnd = aStart + aLength - 1;
    const bEnd = bStart + bLength - 1;
    return aStart <= bEnd && bStart <= aEnd;
}

// Given fixed-width ranges tagged with recordType + a unique `index`, returns the `index` values
// whose [start, start+length) range overlaps another range in the same recordType group.
// Sorting by start first means each range only needs comparing against the current cluster's
// furthest-reaching member (not every other range) - O(n log n) (sort-dominated) instead of an
// O(n^2) all-pairs scan. Classic "merge overlapping intervals."
export const findOverlappingRangeIndexes = <T extends { index: number | null; recordType: string; start: number; length: number }>(
    ranges: T[]
): Set<number> => {
    const invalidIndexes = new Set<number>();
    const groups = new Map<string, T[]>();
    for (const range of ranges) {
        const group = groups.get(range.recordType);
        if (group) {
            group.push(range);
        } else {
            groups.set(range.recordType, [range]);
        }
    }

    for (const group of groups.values()) {
        const sorted = [...group].sort((a, b) => a.start - b.start);
        let cluster: T[] = [];
        let clusterMaxEndRange: T | null = null;

        const flushCluster = () => {
            if (cluster.length > 1) {
                cluster.forEach(r => {
                    if (r.index !== null) {
                        invalidIndexes.add(r.index);
                    }
                });
            }
        };

        for (const range of sorted) {
            if (clusterMaxEndRange && doRangesOverlap(range.start, range.length, clusterMaxEndRange.start, clusterMaxEndRange.length)) {
                cluster.push(range);
                if (range.start + range.length > clusterMaxEndRange.start + clusterMaxEndRange.length) {
                    clusterMaxEndRange = range;
                }
            } else {
                flushCluster();
                cluster = [range];
                clusterMaxEndRange = range;
            }
        }
        flushCluster();
    }
    return invalidIndexes;
}

export {
    localeCompare,
    checkRole,
    formatDateToLocal,
    generatePagination,
    checkRoleAdmin,
    trimTwoUppercaseCharsAtBeginning,
    currencyFormat,
    monthNameToDateTimeString,
    dateTimeStringToMonthName,
    showFormFields,
    isValidStateProfile,
    redirectPathPage,
    beforeUnloadHandler,
    showNewFormFields,
}